import { html, nothing } from "lit";
import "../chat/rich-runtime-block.ts";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { renderRichIframeBlock, renderRichSvgBlock } from "../chat/rich-static-block.ts";
import { icons } from "../icons.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import type { ChatSidebarBlock, ChatSidebarContent, ChatSidebarStructuredContent } from "../types/chat-types.ts";

export type MarkdownSidebarProps = {
  content: ChatSidebarContent | null;
  error: string | null;
  onClose: () => void;
  onViewRawText: () => void;
};

function isStructuredSidebarContent(
  content: ChatSidebarContent | null,
): content is ChatSidebarStructuredContent {
  return Boolean(content && typeof content === "object" && content.kind === "structured");
}

function renderStructuredSidebarBlock(block: ChatSidebarBlock) {
  switch (block.type) {
    case "markdown":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--markdown">
          ${block.label ? html`<div class="sidebar-structured-block__label">${block.label}</div>` : nothing}
          <div class="sidebar-markdown">${unsafeHTML(toSanitizedMarkdownHtml(block.content))}</div>
        </section>
      `;
    case "json": {
      const pretty = JSON.stringify(block.data, null, 2);
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--json">
          ${block.label ? html`<div class="sidebar-structured-block__label">${block.label}</div>` : nothing}
          <pre class="sidebar-structured-code"><code>${pretty}</code></pre>
        </section>
      `;
    }
    case "code":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--code">
          ${block.label ? html`<div class="sidebar-structured-block__label">${block.label}</div>` : nothing}
          ${block.language
            ? html`<div class="sidebar-structured-block__meta">${block.language}</div>`
            : nothing}
          <pre class="sidebar-structured-code"><code>${block.content}</code></pre>
        </section>
      `;
    case "image":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--image">
          ${block.label ? html`<div class="sidebar-structured-block__label">${block.label}</div>` : nothing}
          <img class="sidebar-structured-image" src=${block.url} alt=${block.alt ?? "Attached image"} />
        </section>
      `;
    case "embedded-markdown":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--markdown">
          ${block.label ? html`<div class="sidebar-structured-block__label">${block.label}</div>` : nothing}
          <div class="sidebar-markdown">${unsafeHTML(toSanitizedMarkdownHtml(block.content))}</div>
        </section>
      `;
    case "diagram-mermaid":
    case "chart-vega-lite":
    case "math-latex":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--rich sidebar-structured-block--runtime">
          <openclaw-rich-runtime-block .block=${block} context="sidebar"></openclaw-rich-runtime-block>
        </section>
      `;
    case "svg":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--rich sidebar-structured-block--static">
          ${renderRichSvgBlock(block, "sidebar")}
        </section>
      `;
    case "iframe":
      return html`
        <section class="sidebar-structured-block sidebar-structured-block--rich sidebar-structured-block--static">
          ${renderRichIframeBlock(block, "sidebar")}
        </section>
      `;
  }
}

export function renderMarkdownSidebar(props: MarkdownSidebarProps) {
  const structured = isStructuredSidebarContent(props.content) ? props.content : null;
  const rawMarkdown = typeof props.content === "string" ? props.content : null;
  const title = structured?.title ?? "Tool Output";
  const canViewRawText = Boolean((structured?.rawText ?? rawMarkdown)?.trim());

  return html`
    <div class="sidebar-panel">
      <div class="sidebar-header">
        <div class="sidebar-title">${title}</div>
        <div class="sidebar-header__actions">
          ${canViewRawText
            ? html`
                <button @click=${props.onViewRawText} class="btn" title="View raw text">
                  View Raw Text
                </button>
              `
            : nothing}
          <button @click=${props.onClose} class="btn" title="Close sidebar">
            ${icons.x}
          </button>
        </div>
      </div>
      <div class="sidebar-content">
        ${
          props.error
            ? html`
                <div class="callout danger">${props.error}</div>
              `
            : structured
              ? html`
                  <div class="sidebar-structured">
                    ${structured.blocks.map((block) => renderStructuredSidebarBlock(block))}
                  </div>
                `
              : rawMarkdown
                ? html`<div class="sidebar-markdown">${unsafeHTML(toSanitizedMarkdownHtml(rawMarkdown))}</div>`
                : html`
                    <div class="muted">No content available</div>
                  `
        }
      </div>
    </div>
  `;
}
